import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PauseCircle, PlayCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const POLL_MS = 60_000;

const INITIAL = { env_frozen: false, runtime_frozen: false, loaded: false };

/**
 * QBO write kill-switch banner.
 *
 * Two independent freezes (writes blocked if EITHER is on):
 *  - env_frozen      → FREEZE_QBO_WRITES env var (managed from the Emergent panel).
 *  - runtime_frozen  → instant dashboard flag, toggled here by an admin.
 *
 * Admins always see a control (amber when frozen, slim neutral bar when active)
 * so they can pause/resume QBO writes instantly. Non-admins only see the amber
 * banner while frozen.
 */
export default function QboFreezeBanner() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [state, setState] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const pendingActivate = useRef(false);
  const mounted = useRef(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/admin/qbo-freeze-status');
      if (!mounted.current) return;
      setState({
        env_frozen: !!res.data?.env_frozen,
        runtime_frozen: !!res.data?.runtime_frozen,
        loaded: true,
      });
    } catch {
      // 403 (non-admin/accountant) or network error → keep hidden.
      if (mounted.current) setState({ ...INITIAL, loaded: true });
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchStatus();
    const timer = setInterval(fetchStatus, POLL_MS);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [fetchStatus]);

  const requestToggle = (activate) => {
    pendingActivate.current = activate;
    setDialogOpen(true);
  };

  const confirmToggle = async () => {
    const activate = pendingActivate.current;
    setBusy(true);
    try {
      await api.post(`/admin/runtime-freeze?active=${activate}`);
      toast.success(
        activate
          ? 'Escrituras a QBO PAUSADAS (freeze de runtime activado)'
          : 'Freeze de runtime desactivado — escrituras a QBO reanudadas',
      );
      await fetchStatus();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'No se pudo cambiar el freeze de runtime',
      );
    } finally {
      if (mounted.current) {
        setBusy(false);
        setDialogOpen(false);
      }
    }
  };

  const { env_frozen, runtime_frozen, loaded } = state;
  const frozen = env_frozen || runtime_frozen;

  if (!loaded) return null;
  if (!frozen && !isAdmin) return null;

  // ── Active + admin → slim neutral control bar ────────────────────────────
  if (!frozen) {
    return (
      <>
        <div
          data-testid="qbo-active-bar"
          className="sticky top-0 z-40 w-full"
          style={{
            background: 'rgba(16,185,129,0.08)',
            borderBottom: '1px solid rgba(16,185,129,0.30)',
            color: '#34D399',
          }}
        >
          <div className="mx-auto flex w-full max-w-[1400px] items-center gap-3 px-6 py-1.5 sm:px-7">
            <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-[#10B981]" />
            <div className="flex-1 min-w-0 text-[11px] font-medium tracking-tight text-[#6EE7B7]">
              Integración QBO activa — las ventas de TikTok se replican a QuickBooks Online.
            </div>
            <button
              type="button"
              data-testid="qbo-runtime-freeze-toggle"
              onClick={() => requestToggle(true)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#F59E0B]/50 bg-[#F59E0B]/10 px-3 py-[5px] text-[11px] font-semibold uppercase tracking-[0.06em] text-[#FBBF24] transition-colors hover:bg-[#F59E0B]/20 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PauseCircle className="h-3.5 w-3.5" />}
              Pausar QBO
            </button>
          </div>
        </div>
        <ConfirmDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          activate={pendingActivate.current}
          busy={busy}
          onConfirm={confirmToggle}
        />
      </>
    );
  }

  // ── Frozen → amber banner (+ admin toggle for the runtime flag) ──────────
  return (
    <>
      <div
        data-testid="qbo-freeze-banner"
        role="status"
        aria-live="polite"
        className="sticky top-0 z-40 w-full"
        style={{
          background: 'linear-gradient(90deg, rgba(245,158,11,0.18) 0%, rgba(245,158,11,0.10) 100%)',
          borderBottom: '1px solid rgba(245,158,11,0.45)',
          color: '#FCD34D',
        }}
      >
        <div className="mx-auto flex w-full max-w-[1400px] items-center gap-3 px-6 py-2.5 sm:px-7">
          <PauseCircle className="h-[18px] w-[18px] shrink-0 text-[#F59E0B]" strokeWidth={2} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[12px] font-semibold tracking-tight text-[#FBBF24] uppercase">
                Integración QBO en pausa — escrituras congeladas
              </span>
              {env_frozen && (
                <span
                  data-testid="qbo-freeze-env-pill"
                  className="inline-flex items-center rounded-full border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-2 py-[2px] text-[9px] font-mono font-bold uppercase tracking-[0.08em] text-[#FBBF24]"
                  title="Congelado por la variable de entorno FREEZE_QBO_WRITES"
                >
                  ENV · panel de Emergent
                </span>
              )}
              {runtime_frozen && (
                <span
                  data-testid="qbo-freeze-runtime-pill"
                  className="inline-flex items-center rounded-full border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-2 py-[2px] text-[9px] font-mono font-bold uppercase tracking-[0.08em] text-[#FBBF24]"
                  title="Freeze de runtime — desactivable desde aquí"
                >
                  RUNTIME · desactivable aquí
                </span>
              )}
            </div>
            <div className="text-[11px] leading-snug text-[#FCD34D]/85 mt-0.5">
              {runtime_frozen && !env_frozen
                ? 'Freno instantáneo activado desde el dashboard. Puedes reactivar las escrituras desde aquí.'
                : env_frozen && !runtime_frozen
                  ? 'Congelado por variable de entorno (FREEZE_QBO_WRITES). Se gestiona desde el panel de Emergent.'
                  : 'Congelado por variable de entorno Y por el freno de runtime. Reactivar el runtime no basta: también hay que desactivar el env var.'}
            </div>
          </div>

          {isAdmin && runtime_frozen && (
            <button
              type="button"
              data-testid="qbo-runtime-unfreeze-toggle"
              onClick={() => requestToggle(false)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#10B981]/50 bg-[#10B981]/10 px-3 py-[5px] text-[11px] font-semibold uppercase tracking-[0.06em] text-[#34D399] transition-colors hover:bg-[#10B981]/20 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
              Reactivar
            </button>
          )}
          {isAdmin && !runtime_frozen && env_frozen && (
            <button
              type="button"
              data-testid="qbo-runtime-freeze-toggle"
              onClick={() => requestToggle(true)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#F59E0B]/50 bg-[#F59E0B]/10 px-3 py-[5px] text-[11px] font-semibold uppercase tracking-[0.06em] text-[#FBBF24] transition-colors hover:bg-[#F59E0B]/20 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PauseCircle className="h-3.5 w-3.5" />}
              Freeze runtime
            </button>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        activate={pendingActivate.current}
        busy={busy}
        onConfirm={confirmToggle}
      />
    </>
  );
}

function ConfirmDialog({ open, onOpenChange, activate, busy, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="qbo-freeze-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {activate ? 'Pausar escrituras a QuickBooks Online' : 'Reactivar escrituras a QuickBooks Online'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {activate
              ? 'Se bloquearán INMEDIATAMENTE todas las escrituras a QBO (job automático y endpoints admin). Las ventas se siguen guardando en la base local. Aplica en segundos, sin reinicio.'
              : 'Se reanudarán las escrituras a QBO en segundos. (Si FREEZE_QBO_WRITES sigue activo por variable de entorno, las escrituras seguirán congeladas hasta cambiarlo en el panel de Emergent.)'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="qbo-freeze-confirm-cancel" disabled={busy}>
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="qbo-freeze-confirm-accept"
            disabled={busy}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            {busy ? 'Aplicando…' : activate ? 'Sí, pausar' : 'Sí, reactivar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
